import { isPlatformBrowser } from '@angular/common';
import {
  Directive, ElementRef, Inject, Input, OnDestroy, PLATFORM_ID, AfterViewInit, OnInit, SimpleChanges, OnChanges
} from '@angular/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { LazyLoadObserverService } from './lazy-load-observer.service';
import { LazyDataInterface } from './lazy-load.interface';


@Directive({
  selector: '[lazySrc]'
})
export class LazyLoadImageDirective implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() public preRender: boolean = false;
  @Input() public lazySrc: LazyDataInterface | undefined = undefined;

  private readonly ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private readonly element: ElementRef,
    private readonly lazyLoadObserverService: LazyLoadObserverService,
    @Inject(PLATFORM_ID) private readonly platformId: Record<string, unknown>
  ) {}

  public ngOnInit() {
    const url = `data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${this.lazySrc?.width} ${this.lazySrc?.height}"%3E%3C/svg%3E`;
    this.element.nativeElement.setAttribute('src', url);
  }

  public ngAfterViewInit() {
    this.setAnimation(this.element.nativeElement);
    if (!isPlatformBrowser(this.platformId)) {
      if (this.preRender) {
        this.load();
        return;
      }
    }

    this.lazyLoadObserverService.observeElement(this.element.nativeElement).pipe(
      takeUntil(this.ngUnsubscribe),
      take(1)
    ).subscribe(() => {
      this.load();
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (!changes.lazySrc.firstChange && changes.lazySrc.currentValue.src !== changes.lazySrc.previousValue?.src) {
      this.ngAfterViewInit();
    }
  }

  public setAnimation(element: HTMLElement) {
    // In order to include the element in the viewport in case of the element to be placed bottom
    if (this.preRender) {
      return;
    }

    element.style.minHeight = '5px';
    element.style.opacity = '0';
    element.style.transition = 'opacity .5s';
    element.onload = () => {
      element.style.opacity = '1';
    };
  }

  public ngOnDestroy() {
    this.lazyLoadObserverService.unobserveElement(this.element.nativeElement);
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private load(): void {
    this.element.nativeElement.style.minHeight = 'auto';
    this.element.nativeElement.setAttribute('src', this.lazySrc?.src);
    if (this.preRender) {
      this.element.nativeElement.classList.add('fade-in');
    }
  }

}
